import { useEffect, useState, type CSSProperties } from 'react';
import {
  BANNER_SEVERITY_THEMES,
  autoTextColorForBg,
  dismissBanner,
  listActiveBanners,
  logBannerClick,
  logBannerView,
} from '@/lib/banners';
import type { ActiveBanner } from '@/lib/types';

/**
 * Active banners bar — modern card-style banners stacked at the top of
 * AppShell. Three layout variants:
 *   compact     — single-row strip with optional small icon (default)
 *   media_left  — 64x64 image on the left, larger title/body, CTA right
 *   media_bg    — image as soft background overlay (good for promo)
 *
 * Each banner is dismissible (or acknowledgement-required, in which case
 * the dismiss button reads "I acknowledge"). Promo banners can be globally
 * muted via /settings.
 */
export function ActiveBanners() {
  const [banners, setBanners] = useState<ActiveBanner[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listActiveBanners()
      .then((rows) => {
        if (cancelled) return;
        setBanners(rows);
        // Fire view-events for each banner the user just saw. Server dedupes
        // per (user, banner, UTC day) so calling on every mount is safe.
        for (const b of rows) {
          void logBannerView(b.id).catch(() => undefined);
        }
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onCtaClick(b: ActiveBanner) {
    void logBannerClick(b.id).catch(() => undefined);
  }

  async function onDismiss(b: ActiveBanner) {
    setBusy(b.id);
    try {
      await dismissBanner(b.id, b.acknowledgement_required);
      setBanners((cur) => cur.filter((x) => x.id !== b.id));
    } finally {
      setBusy(null);
    }
  }

  if (banners.length === 0) return null;

  return (
    <div className="space-y-2 px-3 pt-3">
      {banners.map((b) => (
        <BannerCard
          key={b.id}
          banner={b}
          busy={busy === b.id}
          onDismiss={() => onDismiss(b)}
          onCtaClick={() => onCtaClick(b)}
        />
      ))}
    </div>
  );
}

function BannerCard({
  banner: b,
  busy,
  onDismiss,
  onCtaClick,
}: {
  banner: ActiveBanner;
  busy: boolean;
  onDismiss: () => void;
  onCtaClick: () => void;
}) {
  const theme = BANNER_SEVERITY_THEMES[b.severity];
  const dismissLabel = b.acknowledgement_required ? 'I acknowledge' : 'Dismiss';
  const hasCustom = !!b.custom_bg_hex;
  const customBg = b.custom_bg_hex ?? undefined;
  const customFg = b.custom_text_hex ?? (b.custom_bg_hex ? autoTextColorForBg(b.custom_bg_hex) : undefined);
  const customStyle: CSSProperties | undefined = hasCustom
    ? { background: customBg, color: customFg } : undefined;
  const customCtaStyle: CSSProperties | undefined = hasCustom
    ? { background: customFg, color: customBg } : undefined;
  const wrapperBgClass = hasCustom ? '' : theme.gradient;
  const wrapperFgClass = hasCustom ? '' : theme.text;
  const ctaClass = hasCustom ? '' : theme.cta;

  if (b.layout === 'media_bg' && b.image_url) {
    return (
      <div
        className={`${wrapperBgClass} ${wrapperFgClass} container mx-auto relative overflow-hidden rounded-xl shadow-lg ring-1 ${theme.ring}`}
        style={customStyle}
        role="region"
        aria-label={`${theme.label} banner: ${b.title}`}
      >
        <img
          src={b.image_url}
          alt={b.image_alt ?? ''}
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          loading="lazy"
        />
        <div className="relative px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold drop-shadow-sm">{b.title}</p>
            {b.body_md && (
              <p className="text-sm whitespace-pre-wrap opacity-95 drop-shadow-sm">
                {b.body_md}
              </p>
            )}
          </div>
          <BannerActions
            cta={b.cta_text && b.cta_url ? { text: b.cta_text, url: b.cta_url } : null}
            ctaClass={ctaClass}
            ctaStyle={customCtaStyle}
            dismissLabel={dismissLabel}
            busy={busy}
            onDismiss={onDismiss}
            onCtaClick={onCtaClick}
          />
        </div>
      </div>
    );
  }

  if (b.layout === 'media_left' && b.image_url) {
    return (
      <div
        className={`${wrapperBgClass} ${wrapperFgClass} container mx-auto rounded-xl shadow-lg ring-1 ${theme.ring} px-4 py-3`}
        style={customStyle}
        role="region"
        aria-label={`${theme.label} banner: ${b.title}`}
      >
        <div className="flex flex-wrap items-center gap-4">
          <img
            src={b.image_url}
            alt={b.image_alt ?? ''}
            className="h-16 w-16 rounded-lg object-cover bg-white/20 shrink-0"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold">{b.title}</p>
            {b.body_md && (
              <p className="text-sm whitespace-pre-wrap opacity-95">{b.body_md}</p>
            )}
          </div>
          <BannerActions
            cta={b.cta_text && b.cta_url ? { text: b.cta_text, url: b.cta_url } : null}
            ctaClass={ctaClass}
            ctaStyle={customCtaStyle}
            dismissLabel={dismissLabel}
            busy={busy}
            onDismiss={onDismiss}
            onCtaClick={onCtaClick}
          />
        </div>
      </div>
    );
  }

  // compact (default)
  return (
    <div
      className={`${wrapperBgClass} ${wrapperFgClass} container mx-auto rounded-lg shadow-sm ring-1 ${theme.ring} px-4 py-2.5`}
      style={customStyle}
      role="region"
      aria-label={`${theme.label} banner: ${b.title}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 flex items-start gap-3">
          {b.image_url && (
            <img
              src={b.image_url}
              alt={b.image_alt ?? ''}
              className="h-9 w-9 rounded-md object-cover bg-white/20 shrink-0"
              loading="lazy"
            />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">{b.title}</p>
            {b.body_md && (
              <p className="text-xs whitespace-pre-wrap opacity-95 mt-0.5">
                {b.body_md}
              </p>
            )}
          </div>
        </div>
        <BannerActions
          cta={b.cta_text && b.cta_url ? { text: b.cta_text, url: b.cta_url } : null}
          ctaClass={ctaClass}
          ctaStyle={customCtaStyle}
          dismissLabel={dismissLabel}
          busy={busy}
          onDismiss={onDismiss}
          onCtaClick={onCtaClick}
        />
      </div>
    </div>
  );
}

function BannerActions({
  cta,
  ctaClass,
  ctaStyle,
  dismissLabel,
  busy,
  onDismiss,
  onCtaClick,
}: {
  cta: { text: string; url: string } | null;
  ctaClass: string;
  ctaStyle?: CSSProperties;
  dismissLabel: string;
  busy: boolean;
  onDismiss: () => void;
  onCtaClick: () => void;
}) {
  return (
    <div className="flex items-center gap-3 shrink-0">
      {cta && (
        <a
          href={cta.url}
          target="_blank"
          rel="noreferrer"
          onClick={onCtaClick}
          onAuxClick={onCtaClick}
          style={ctaStyle}
          className={`${ctaClass} rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm`}
        >
          {cta.text}
        </a>
      )}
      <button
        type="button"
        onClick={onDismiss}
        disabled={busy}
        className="text-xs font-medium underline-offset-2 hover:underline disabled:opacity-50"
      >
        {dismissLabel}
      </button>
    </div>
  );
}
